import type { ReactNode } from 'react';
import { Alert, Badge, Icon, SideDrawer, linkTextClasses } from '@evogard/ui';
import type { FinanceExpenseClaim } from '@evogard/contract';
import {
  EXPENSE_REQUEST_STAGE_LABELS,
  formatDocument,
  formatInstant,
  formatPhone,
} from '@evogard/domain';
import { expenseAmount } from '../../../components/expense/SupervisorExpenseBoard.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { useFinanceClaim } from './useFinanceDesk.js';

/**
 * A gaveta do sinistro na mesa da despesa (28/10/2026, pedido do Paulo): o
 * Financeiro lê de onde veio o pedido sem sair da mesa — onde o sinistro
 * está, o que aconteceu, de quem é o carro e as outras despesas que ele já
 * pediu. O sinistro inteiro continua a um clique, em outra aba, para quem
 * pode abri-lo.
 */
export function FinanceClaimDrawer({
  expenseId,
  onClose,
}: {
  expenseId: number;
  onClose: () => void;
}) {
  const { data: context } = useSessionContext();
  const query = useFinanceClaim(expenseId);
  const c = query.data;
  const canOpen = can(context, 'claims.notices', 'ver');

  return (
    <SideDrawer
      eyebrow="Pedido de origem"
      title={c ? `Sinistro #${c.id}` : 'Carregando…'}
      closeLabel="Fechar o sinistro"
      onClose={onClose}
    >
      {query.isError ? (
        <Alert tone="erro">{query.error.message}</Alert>
      ) : !c ? (
        <p className="text-sm text-texto-suave">Carregando o sinistro…</p>
      ) : (
        <div className="flex flex-col gap-5 text-sm">
          <VehicleSituation situation={c.vehicle.situation} plate={c.vehicle.plate} />
          <Section title="Situação">
            <Row label="Departamento">{c.department}</Row>
            <Row label="Fase">{c.phase}</Row>
            <Row label="Evento">{c.eventType}</Row>
            <Row label="Ocorrido em">{formatInstant(c.occurredAt)}</Row>
            <Row label="Avisado em">{formatInstant(c.notifiedAt)}</Row>
          </Section>

          <Section title="Associado e veículo">
            <Row label="Associado">
              {c.member ? (c.member.name ?? c.member.code) : null}
            </Row>
            {c.member?.document && (
              <Row label="Documento">{formatDocument(c.member.document)}</Row>
            )}
            {c.member?.phone && <Row label="Telefone">{formatPhone(c.member.phone)}</Row>}
            <Row label="Placa">
              {c.vehicle.plate ? <span className="tabular-nums">{c.vehicle.plate}</span> : null}
            </Row>
            <Row label="Veículo">{c.vehicle.model}</Row>
            {c.driverName && <Row label="Condutor">{c.driverName}</Row>}
          </Section>

          <section aria-label="O ocorrido" className="flex flex-col gap-2">
            <SectionTitle>O ocorrido</SectionTitle>
            <p className="whitespace-pre-wrap">{c.description}</p>
          </section>

          <section aria-label="Despesas deste sinistro" className="flex flex-col gap-2">
            <SectionTitle>Despesas deste sinistro ({c.expenses.length})</SectionTitle>
            <ul className="flex flex-col divide-y divide-borda-suave rounded-borda border border-borda">
              {c.expenses.map((x) => (
                <li key={x.id} className="flex flex-col gap-0.5 px-3 py-2">
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0 font-semibold">
                      #{x.id} · {x.beneficiaryName ?? 'Beneficiário sem nome'}
                    </span>
                    <span className="shrink-0 tabular-nums">{expenseAmount(x.amount)}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-1.5 text-xs text-texto-suave">
                    {EXPENSE_REQUEST_STAGE_LABELS[x.stage]} · {x.status}
                    {x.current && <Badge tone="informacao">Esta despesa</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {canOpen && (
            <a
              href={`/claims/${c.id}`}
              target="_blank"
              rel="noopener"
              className={linkTextClasses('justify-start')}
            >
              Abrir o sinistro completo em outra aba
            </a>
          )}
        </div>
      )}
    </SideDrawer>
  );
}

/**
 * O fundo e a cor do texto de cada tom — os mesmos pares do `Badge`, num
 * bloco: a situação do carro é a primeira coisa que o Financeiro lê.
 */
// @valor
const SITUATION_CLASSES: Readonly<Record<NonNullable<FinanceExpenseClaim['vehicle']['situation']>['tone'], string>> = {
  sucesso: 'border-sucesso bg-sucesso-suave text-sucesso',
  erro: 'border-erro bg-erro-suave text-erro',
  alerta: 'border-alerta bg-alerta-suave text-alerta',
  neutro: 'border-borda bg-superficie-suave text-texto',
};

/**
 * A situação do carro no ERP, em destaque no alto da gaveta (28/10/2026,
 * pedido do Paulo), com a cor de `vehicleSituationTone` — que só pinta o que
 * a casa já julgou; o resto fica neutro.
 */
function VehicleSituation({
  situation,
  plate,
}: {
  situation: FinanceExpenseClaim['vehicle']['situation'];
  plate: string | null;
}) {
  return (
    <section
      aria-label="Situação do veículo"
      className={`flex items-center gap-3 rounded-borda border px-3.5 py-3 ${
        situation ? SITUATION_CLASSES[situation.tone] : SITUATION_CLASSES.neutro
      }`}
    >
      <Icon nome="veiculo" size={24} className="shrink-0" />
      <div className="flex min-w-0 flex-col">
        <span className="text-xs font-medium opacity-80">
          Situação do veículo{plate ? ` · ${plate}` : ''}
        </span>
        <span className="text-base font-bold break-words">
          {situation?.label ?? 'Não sabemos: o carro não está no cadastro da casa'}
        </span>
      </div>
    </section>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h4 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
      {children}
    </h4>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <SectionTitle>{title}</SectionTitle>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">{children}</dl>
    </section>
  );
}

/** Uma linha da ficha; o vazio diz "Não informado", e não some. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-texto-suave">{label}</dt>
      <dd>{children ?? <span className="text-texto-tenue">Não informado</span>}</dd>
    </>
  );
}
